import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteClient,
  getClientLevelsForList,
  getClientsList,
  getClientsListCount,
  getClientSegmentsFiltered,
  getClusters,
  getBranchOfficesFiltered,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { getTablePagination, DEFAULT_TABLE_PAGE_SIZE } from "../../helpers/TablePaginationHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";

import {
  Table,
  Button,
  message,
  Modal,
  Dropdown,
  Menu,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Switch,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { RxPerson } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";

import styles from "./Clients.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

const Clients = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/clients";
    }
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/clients";
    }
    return "/promotions/clients";
  };

  const [data, setData] = useState<any[]>([]);
  const [clientSegments, setClientSegments] = useState<any[]>([]);
  const [clientLevels, setClientLevels] = useState<any[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [countLoading, setCountLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const { beginRequest: beginListRequest, isStaleRequest: isStaleListRequest } =
    useFetchRequestGuard();
  const { beginRequest: beginCountRequest, isStaleRequest: isStaleCountRequest } =
    useFetchRequestGuard();
  const params = new URLSearchParams(location.search);
  const [idNumberFilterValue, setIdNumberFilterValue] = useState<string>(params.get("idNumber") || "");
  const debouncedIdNumberFilterValue = useDebouncedValue(idNumberFilterValue);
  const [externalIdFilterValue, setExternalIdFilterValue] = useState<string>(params.get("externalId") || "");
  const debouncedExternalIdFilterValue = useDebouncedValue(externalIdFilterValue);
  const [segmentFilterValue, setSegmentFilterValue] = useState<string>(params.get("segmentId") || "");
  const [levelFilterValue, setLevelFilterValue] = useState<string>(params.get("levelId") || "");
  const [actualFilterBirthStartDate, setActualFilterBirthStartDate] = useState<any>(params.get("birthStartDate") ? moment(params.get("birthStartDate"), "YYYY-MM-DD") : null);
  const [actualFilterBirthdateEndDate, setActualFilterBirthdateEndDate] = useState<any>(params.get("birthEndDate") ? moment(params.get("birthEndDate"), "YYYY-MM-DD") : null);
  const [actualFilterRegisterStartDate, setActualFilterRegisterStartDate] = useState<any>(params.get("registerStartDate") ? moment(params.get("registerStartDate"), "YYYY-MM-DD") : null);
  const [actualFilterRegisterEndDate, setActualFilterRegisterEndDate] = useState<any>(params.get("registerEndDate") ? moment(params.get("registerEndDate"), "YYYY-MM-DD") : null);
  const [actualPage, setActualPage] = useState(Number(params.get("page")) || 1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [sexFilterValue, setSexFilterValue] = useState<string>(params.get("sex") || "");
  const [clusterFilterValue, setClusterFilterValue] = useState<string>(params.get("clusterId") || "");
  const [branchOfficeFilterValue, setBranchOfficeFilterValue] = useState<string>(params.get("branchOfficeId") || "");
  const [branchOffices, setBranchOffices] = useState<any[]>([]);
  const [showDocumentAsMain, setShowDocumentAsMain] = useState(false);

  const clientFilters = useMemo(
    () => ({
      idNumber:
        debouncedIdNumberFilterValue.trim() === "" ? undefined : debouncedIdNumberFilterValue,
      externalId:
        debouncedExternalIdFilterValue.trim() === "" ? undefined : debouncedExternalIdFilterValue,
      segmentId: segmentFilterValue,
      levelId: levelFilterValue,
      clusterId: clusterFilterValue,
      branchOfficeId: branchOfficeFilterValue,
      birthStartDate: actualFilterBirthStartDate
        ? moment(actualFilterBirthStartDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
      birthEndDate: actualFilterBirthdateEndDate
        ? moment(actualFilterBirthdateEndDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
      registerStartDate: actualFilterRegisterStartDate
        ? moment(actualFilterRegisterStartDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
      registerEndDate: actualFilterRegisterEndDate
        ? moment(actualFilterRegisterEndDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
    }),
    [
      debouncedIdNumberFilterValue,
      debouncedExternalIdFilterValue,
      segmentFilterValue,
      levelFilterValue,
      clusterFilterValue,
      branchOfficeFilterValue,
      actualFilterBirthStartDate,
      actualFilterBirthdateEndDate,
      actualFilterRegisterStartDate,
      actualFilterRegisterEndDate,
    ]
  );

  const fetchClientsList = useCallback(
    async (pagination = actualPage, size = pageSize) => {
      const requestId = beginListRequest();
      setLoading(true);
      try {
        const listResponse = await getClientsList(pagination, clientFilters, size);
        if (isStaleListRequest(requestId)) return;
        setData(listResponse.clients);
      } finally {
        if (!isStaleListRequest(requestId)) {
          setLoading(false);
        }
      }
    },
    [actualPage, pageSize, clientFilters, beginListRequest, isStaleListRequest]
  );

  const fetchClientsCount = useCallback(async () => {
    const requestId = beginCountRequest();
    setCountLoading(true);
    try {
      const countResponse = await getClientsListCount(clientFilters);
      if (isStaleCountRequest(requestId)) return;
      setTotalSize(countResponse.totalElements);
    } finally {
      if (!isStaleCountRequest(requestId)) {
        setCountLoading(false);
      }
    }
  }, [clientFilters, beginCountRequest, isStaleCountRequest]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      fetchClientsCount();
    }
  }, [fetchClientsCount]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      fetchClientsList(actualPage, pageSize);
      updateUrlWithFilters({
        idNumber: idNumberFilterValue,
        externalId: externalIdFilterValue,
        segmentId: segmentFilterValue,
        levelId: levelFilterValue,
        clusterId: clusterFilterValue,
        branchOfficeId: branchOfficeFilterValue,
        birthStartDate: actualFilterBirthStartDate,
        birthEndDate: actualFilterBirthdateEndDate,
        registerStartDate: actualFilterRegisterStartDate,
        registerEndDate: actualFilterRegisterEndDate,
        page: actualPage,
      });
    }
  }, [actualPage, pageSize, fetchClientsList]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleSearchLevels("");
      handleSearchClientSegments("");
      handleSearchClusters("");
      handleSearchBranchOffices("");
    }
  }, []);

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleSearchClientSegments = async (value: string) => {
    const response = await getClientSegmentsFiltered(1, { name: value });
    setClientSegments(response.clientSegments);
  };

  const handleSearchLevels = async (value: string) => {
    const response = await getClientLevelsForList(1, { name: value });
    setClientLevels(response.clientLevels);
  };

  const handleSearchClusters = async (value: string) => {
    const response = await getClusters(1);
    setClusters(response.clusters);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, { name: value });
    setBranchOffices(response.branchOffices);
  };

  const handleChangeBirthDateFilter = (value: any) => {
    setActualFilterBirthStartDate(value ? value[0] ?? null : null);
    setActualFilterBirthdateEndDate(value ? value[1] ?? null : null);
  };

  const handleChangeRegisterDateFilter = (value: any) => {
    setActualFilterRegisterStartDate(value ? value[0] ?? null : null);
    setActualFilterRegisterEndDate(value ? value[1] ?? null : null);
  };

  const showDeleteConfirm = (itemId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteClient(itemId);
      },
    });
  };

  const handleDeleteClient = async (clientId: number) => {
    setLoading(true);
    const deleteStatus = await deleteClient(clientId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchClientsList(actualPage, pageSize);
    fetchClientsCount();
    setLoading(false);
  };

  const handlePagination = (page: number, size: number) => {
    if (size !== pageSize) {
      setPageSize(size);
      setActualPage(1);
      return;
    }
    setActualPage(page);
  };

  const handleClearFilters = () => {
    setIdNumberFilterValue("");
    setExternalIdFilterValue("");
    setSegmentFilterValue("");
    setLevelFilterValue("");
    setClusterFilterValue("");
    setBranchOfficeFilterValue("");
    setActualFilterBirthStartDate(null);
    setActualFilterBirthdateEndDate(null);
    setActualFilterRegisterStartDate(null);
    setActualFilterRegisterEndDate(null);
    setActualPage(1);
  };

  const hasActiveFilters = () => {
    return (
      idNumberFilterValue !== "" ||
      externalIdFilterValue !== "" ||
      segmentFilterValue !== "" ||
      levelFilterValue !== "" ||
      clusterFilterValue !== "" ||
      branchOfficeFilterValue !== "" ||
      actualFilterBirthStartDate !== null ||
      actualFilterBirthdateEndDate !== null ||
      actualFilterRegisterStartDate !== null ||
      actualFilterRegisterEndDate !== null
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="Client" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="Client"
          createPath={`${getBasePath()}/create`}
          createPermission={privilegesEnum.WRITE_CLIENTS}
        />
      );
    }

    return null;
  };

  const columns = [
    {
      title: (
        <span className={styles.identifierHeaderToggle}>
          {showDocumentAsMain ? t("Common-Identifier") : t("Common-ExternalId")}
          <Tooltip
            title={
              showDocumentAsMain
                ? t("Common-HideDocumentInList")
                : t("Common-ShowDocumentInList")
            }
          >
            <Switch
              size="small"
              checked={showDocumentAsMain}
              onChange={setShowDocumentAsMain}
            />
          </Tooltip>
        </span>
      ),
      key: "identifier",
      render: (_: any, record: any) => {
        const externalId = record.externalId ?? record.external_id;
        const idNumber = record.idNumber;
        const extStr =
          externalId != null && String(externalId).trim() !== ""
            ? String(externalId)
            : null;
        const docStr =
          idNumber != null && String(idNumber).trim() !== "" ? String(idNumber) : null;
        const main = showDocumentAsMain
          ? docStr || extStr || "-"
          : (extStr || "-");
        return (
          <div className={styles.identifierCell}>
            <a
              href={`${getBasePath()}/${record.id}/view`}
              className={styles.nameLink}
            >
              {main}
            </a>
          </div>
        );
      },
    },
    {
      title: t("Common-BirthDate"),
      dataIndex: "birthdate",
    },
    {
      title: t("Common-RegisterDate"),
      dataIndex: "createdAt",
      render: (date: string) => date?.split("T")[0],
    },
    {
      title: t("Common-Level"),
      key: "level",
      render: (_: any, record: any) => record.level?.name || "-",
    },
    {
      title: t("Common-Segments"),
      key: "segments",
      render: (_: any, record: any) =>
        record.segments && record.segments.length > 0
          ? record.segments.map((s: any) => s.name).join(", ")
          : "-",
    },
    {
      title: t("Common-TotalAccumulationUnits"),
      dataIndex: "accumulatedUnits",
      render: (value: any) => value ?? "-",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (idNumberFilterValue) params.set("idNumber", idNumberFilterValue);
                  if (externalIdFilterValue) params.set("externalId", externalIdFilterValue);
                  if (segmentFilterValue) params.set("segmentId", segmentFilterValue);
                  if (levelFilterValue) params.set("levelId", levelFilterValue);
                  if (clusterFilterValue) params.set("clusterId", clusterFilterValue);
                  if (branchOfficeFilterValue) params.set("branchOfficeId", branchOfficeFilterValue);
                  if (actualFilterBirthStartDate) params.set("birthStartDate", moment(actualFilterBirthStartDate).format("YYYY-MM-DD"));
                  if (actualFilterBirthdateEndDate) params.set("birthEndDate", moment(actualFilterBirthdateEndDate).format("YYYY-MM-DD"));
                  if (actualFilterRegisterStartDate) params.set("registerStartDate", moment(actualFilterRegisterStartDate).format("YYYY-MM-DD"));
                  if (actualFilterRegisterEndDate) params.set("registerEndDate", moment(actualFilterRegisterEndDate).format("YYYY-MM-DD"));
                  if (actualPage) params.set("page", String(actualPage));
                  navigate(`${getBasePath()}/${record.id}/view?${params.toString()}`);
                }}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_CLIENTS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (idNumberFilterValue) params.set("idNumber", idNumberFilterValue);
                    if (externalIdFilterValue) params.set("externalId", externalIdFilterValue);
                    if (segmentFilterValue) params.set("segmentId", segmentFilterValue);
                    if (levelFilterValue) params.set("levelId", levelFilterValue);
                    if (actualFilterBirthStartDate) params.set("birthStartDate", moment(actualFilterBirthStartDate).format("YYYY-MM-DD"));
                    if (actualFilterBirthdateEndDate) params.set("birthEndDate", moment(actualFilterBirthdateEndDate).format("YYYY-MM-DD"));
                    if (actualFilterRegisterStartDate) params.set("registerStartDate", moment(actualFilterRegisterStartDate).format("YYYY-MM-DD"));
                    if (actualFilterRegisterEndDate) params.set("registerEndDate", moment(actualFilterRegisterEndDate).format("YYYY-MM-DD"));
                    if (actualPage) params.set("page", String(actualPage));
                    navigate(`${getBasePath()}/${record.id}/view?${params.toString()}`);
                  }}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_CLIENTS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const updateUrlWithFilters = (filters: any) => {
    const searchParams = new URLSearchParams();
    if (filters.idNumber) searchParams.set("idNumber", filters.idNumber);
    if (filters.externalId) searchParams.set("externalId", filters.externalId);
    if (filters.segmentId) searchParams.set("segmentId", filters.segmentId);
    if (filters.levelId) searchParams.set("levelId", filters.levelId);
    if (filters.clusterId) searchParams.set("clusterId", filters.clusterId);
    if (filters.branchOfficeId) searchParams.set("branchOfficeId", filters.branchOfficeId);
    if (filters.birthStartDate) searchParams.set("birthStartDate", filters.birthStartDate);
    if (filters.birthEndDate) searchParams.set("birthEndDate", filters.birthEndDate);
    if (filters.registerStartDate) searchParams.set("registerStartDate", filters.registerStartDate);
    if (filters.registerEndDate) searchParams.set("registerEndDate", filters.registerEndDate);
    if (filters.page) searchParams.set("page", String(filters.page));
    navigate({ search: searchParams.toString() }, { replace: true });
  };


  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-Clients")}
        icon={<RxPerson />}
        onCreateClick={() => navigate(`${getBasePath()}/create`)}
        createPermission={privilegesEnum.WRITE_CLIENTS}
      />
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_CLIENTS) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-Identifier")}
                    className={styles.filterInput}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => {
                      setIdNumberFilterValue(e.target.value);
                      setActualPage(1);
                    }}
                    value={idNumberFilterValue}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-ExternalId")}
                    className={styles.filterInput}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => {
                      setExternalIdFilterValue(e.target.value);
                      setActualPage(1);
                    }}
                    value={externalIdFilterValue}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    showSearch
                    onSelect={(_, option) => {
                      if (option) setSegmentFilterValue(option.value);
                      setActualPage(1);
                    }}
                    allowClear
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onClear={() => {
                      setSegmentFilterValue("");
                      setActualPage(1);
                    }}
                    onSearch={handleSearchClientSegments}
                    placeholder={t("Common-ClientSegment")}
                  >
                    {getOptions(clientSegments)}
                  </Select>
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    showSearch
                    onSelect={(_, option) => {
                      if (option) setLevelFilterValue(option.value);
                      setActualPage(1);
                    }}
                    allowClear
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onClear={() => {
                      setLevelFilterValue("");
                      setActualPage(1);
                    }}
                    onSearch={handleSearchLevels}
                    placeholder={t("Common-ClientLevel")}
                  >
                    {getOptions(clientLevels)}
                  </Select>
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    showSearch
                    onSelect={(_, option) => {
                      if (option) setClusterFilterValue(option.value);
                      setActualPage(1);
                    }}
                    allowClear
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onClear={() => {
                      setClusterFilterValue("");
                      setActualPage(1);
                    }}
                    onSearch={handleSearchClusters}
                    placeholder={t("Common-Cluster")}
                  >
                    {getOptions(clusters)}
                  </Select>
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    showSearch
                    onSelect={(_, option) => {
                      if (option) setBranchOfficeFilterValue(String(option.value));
                      setActualPage(1);
                    }}
                    allowClear
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onClear={() => {
                      setBranchOfficeFilterValue("");
                      setActualPage(1);
                    }}
                    onSearch={handleSearchBranchOffices}
                    placeholder={t("Common-BranchOffice")}
                    value={branchOfficeFilterValue ? Number(branchOfficeFilterValue) : undefined}
                  >
                    {getOptions(branchOffices)}
                  </Select>
                </div>
                <div className={styles.filterWrapper}>
                  <DatePicker.RangePicker
                    className={styles.dateSelector}
                    format="YYYY-MM-DD"
                    allowEmpty={[true, true]}
                    onChange={handleChangeBirthDateFilter}
                    onCalendarChange={handleChangeBirthDateFilter}
                    placeholder={[
                      t("Common-StartDateBirthdate"),
                      t("Common-EndDateBirthdate"),
                    ]}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <DatePicker.RangePicker
                    className={styles.dateSelector}
                    format="YYYY-MM-DD"
                    allowEmpty={[true, true]}
                    onChange={handleChangeRegisterDateFilter}
                    onCalendarChange={handleChangeRegisterDateFilter}
                    placeholder={[
                      t("Common-StartDateRegister"),
                      t("Common-EndDateRegister"),
                    ]}
                  />
                </div>
                {hasActiveFilters() && (
                  <div className={styles.filterWrapper}>
                    <Tooltip title={t("Common-Clear")}>
                      <Button
                        onClick={handleClearFilters}
                        className={styles.clearButton}
                        icon={<ClearOutlined />}
                      >
                        {t("Common-Clear")}
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            <Table
              size="small"
              className={styles.table}
              loading={loading}
              rowKey={(record) => record.id}
              columns={columns}
              dataSource={data}
              pagination={getTablePagination(t, {
                current: actualPage,
                total: totalSize,
                pageSize,
                dataLength: data.length,
                countLoading,
                showSizeChanger: true,
                onChange: handlePagination,
              })}
              locale={{
                emptyText: getEmptyState(),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
